import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { impactplan100PercentAPI } from '../services/api';
import toast from 'react-hot-toast';

interface ImpactPlan100PercentForm {
  id: number;
  serviceLineNumber: string;
  customerName?: string;
  customerId?: string;
  kitNumber?: string;
  packageLimit: number;
  currentUsage: number;
  usagePercentage: number;
  reachedAt?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
}

// Utility function to format numbers with commas
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

// Helper function to get usage color
const getUsageColor = (percentage: number): string => {
  if (percentage >= 100) return 'text-red-600';
  if (percentage >= 90) return 'text-orange-600';
  return 'text-green-600';
};

const ImpactPlanDetails: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [selectedForm, setSelectedForm] = useState<ImpactPlan100PercentForm | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch Impact Plan 100% usage forms
  const { data: forms = [], isLoading, error, refetch } = useQuery<ImpactPlan100PercentForm[]>(
    'impactplan-100-percent',
    () => impactplan100PercentAPI.getAll(),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Fetch statistics
  const { data: stats } = useQuery(
    'impactplan-100-percent-stats',
    () => impactplan100PercentAPI.getStatistics()
  );

  // Filter forms based on search, status, month, and year
  const filteredForms = forms.filter(form => {
    const matchesSearch = 
      (form.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      form.serviceLineNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (form.kitNumber?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (form.customerId?.toLowerCase().includes(searchTerm.toLowerCase()) || false);

    const matchesStatus = 
      filterStatus === 'all' || 
      (filterStatus === 'active' && form.isActive) ||
      (filterStatus === 'inactive' && !form.isActive);

    // Month and year filtering
    let matchesMonth = true;
    let matchesYear = true;

    if (form.reachedAt) {
      const reachedDate = new Date(form.reachedAt);
      const reachedMonth = reachedDate.getMonth() + 1; // getMonth() returns 0-11, we want 1-12
      const reachedYear = reachedDate.getFullYear();

      if (filterMonth !== 'all') {
        matchesMonth = reachedMonth === parseInt(filterMonth);
      }

      if (filterYear !== 'all') {
        matchesYear = reachedYear === parseInt(filterYear);
      }
    } else {
      // If no reachedAt date, exclude from month/year filters
      if (filterMonth !== 'all' || filterYear !== 'all') {
        matchesMonth = false;
        matchesYear = false;
      }
    }

    return matchesSearch && matchesStatus && matchesMonth && matchesYear;
  });

  const handleViewDetails = (form: ImpactPlan100PercentForm) => {
    setSelectedForm(form);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedForm(null);
  };

  // Export Impact Plan 100% usage forms to Excel
  const handleExport = async () => {
    try {
      const blob = await impactplan100PercentAPI.export();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on filters
      let filename = 'impactplan_100_percent_usage';
      if (filterMonth !== 'all' && filterYear !== 'all') {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[parseInt(filterMonth) - 1];
        filename += `_${monthName}_${filterYear}`;
      } else if (filterYear !== 'all') {
        filename += `_${filterYear}`;
      } else if (filterMonth !== 'all') {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];
        const monthName = monthNames[parseInt(filterMonth) - 1];
        filename += `_${monthName}`;
      }
      filename += `_${new Date().toISOString().slice(0,10)}.xlsx`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Impact Plan 100% usage data exported successfully 📄');
    } catch (error: any) {
      console.error('Export failed:', error);
      toast.error(`Failed to export Impact Plan 100% usage data: ${error.response?.data?.message || error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Impact Plan 100% usage forms...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <p className="text-gray-600 font-medium">Failed to load Impact Plan 100% usage forms</p>
          <button
            onClick={() => refetch()}
            className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(244,63,94,0.26),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.22),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-rose-400 shadow-[0_0_0_4px_rgba(251,113,133,0.14)]" />
              Critical usage
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Impact Plan 100% Usage</h1>
            <p className="max-w-2xl text-white/70">
              Impact Plan service lines that have reached (or exceeded) their usage capacity.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-exclamation-triangle text-white/60"></i>
                {filteredForms.length} records
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/impact-plan')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Impact Plan
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-gray-600 font-medium text-sm">Total Service Lines</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg">
                <i className="fas fa-file-alt text-lg"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
                <p className="text-gray-600 font-medium text-sm">Active</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
                <i className="fas fa-check-circle text-lg"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-orange-600">{stats.thisMonth}</p>
                <p className="text-gray-600 font-medium text-sm">This Month</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg">
                <i className="fas fa-calendar text-lg"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-blue-600">{stats.thisWeek}</p>
                <p className="text-gray-600 font-medium text-sm">This Week</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
                <i className="fas fa-clock text-lg"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              className="w-full rounded-2xl bg-gray-50 px-5 py-3 pl-12 text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder="Search service lines, kit numbers, customer names..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            >
              <option value="all">All Records</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>

            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            >
              <option value="all">All Months</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>

            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="rounded-2xl bg-gray-50 px-4 py-3 font-semibold text-gray-900 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            >
              <option value="all">All Years</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
            </select>
            
            <button
              onClick={handleExport}
              className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-3 rounded-2xl hover:brightness-110 transition-all duration-200 font-semibold shadow-lg"
            >
              <i className="fas fa-file-excel mr-2"></i>
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Forms Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-black text-gray-900">Records</h3>
              <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                {filteredForms.length}
              </span>
            </div>
            <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</div>
          </div>
        </div>
        <table className="w-full forms-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '22%' }}>
                  Customer
                </th>
                <th className="px-3 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '20%' }}>
                  Service Line
                </th>
                <th className="px-3 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '18%' }}>
                  Usage
                </th>
                <th className="px-3 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '15%' }}>
                  Reached At
                </th>
                <th className="px-3 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '10%' }}>
                  Status
                </th>
                <th className="px-2 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wider" style={{ width: '8%' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredForms.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <i className="fas fa-file-alt text-4xl mb-4 text-gray-300"></i>
                      <p className="text-lg font-medium">No forms found</p>
                      <p className="text-sm mt-2 text-gray-600">
                        {searchTerm ? 'No forms match your search criteria. Try adjusting your search terms.' : 'No Impact Plan service lines at 100% usage.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredForms.map((form, index) => (
                  <tr key={form.id} className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-3 py-4" style={{ width: '22%' }}>
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-bold text-gray-900">
                            {form.customerName || form.serviceLineNumber}
                          </div>
                          {form.customerId && (
                            <div className="text-sm text-gray-500">
                              ID: {form.customerId}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4" style={{ width: '20%' }}>
                      <div className="text-sm font-semibold text-gray-900">{form.serviceLineNumber}</div>
                      {form.kitNumber && (
                        <div className="text-sm text-gray-500">Kit: {form.kitNumber}</div>
                      )}
                    </td>
                    <td className="px-3 py-4" style={{ width: '18%' }}>
                      <div className="flex items-center flex-wrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatNumber(form.currentUsage)} / {formatNumber(form.packageLimit)} GB
                        </div>
                        <div className={`ml-2 text-sm font-bold ${getUsageColor(Number(form.usagePercentage))}`}>
                          {Number(form.usagePercentage).toFixed(1)}%
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4" style={{ width: '15%' }}>
                      <div className="text-sm text-gray-900">
                        {form.reachedAt ? new Date(form.reachedAt).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 py-4" style={{ width: '10%' }}>
                      <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${form.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {form.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-2 py-4 text-right" style={{ width: '8%' }}>
                      <button
                        onClick={() => handleViewDetails(form)}
                        className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-xl transition-all duration-200 group"
                        title="View Details"
                      >
                        <i className="fas fa-eye text-base group-hover:scale-110 transition-transform"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Service Line Details - {selectedForm.serviceLineNumber}
                </h2>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name
                    </label>
                    <p className="text-gray-900">{selectedForm.customerName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Line
                    </label>
                    <p className="text-gray-900">{selectedForm.serviceLineNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kit Number
                    </label>
                    <p className="text-gray-900">{selectedForm.kitNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer ID
                    </label>
                    <p className="text-gray-900">{selectedForm.customerId || 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Package Limit
                    </label>
                    <p className="text-gray-900">{formatNumber(selectedForm.packageLimit)} GB</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Usage
                    </label>
                    <p className="text-gray-900">{formatNumber(selectedForm.currentUsage)} GB</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Usage Percentage
                    </label>
                    <p className={`font-bold ${getUsageColor(Number(selectedForm.usagePercentage))}`}>
                      {Number(selectedForm.usagePercentage).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reached 100% At
                    </label>
                    <p className="text-gray-900">
                      {selectedForm.reachedAt ? new Date(selectedForm.reachedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {selectedForm.notes && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900">{selectedForm.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpactPlanDetails;
